// This is a skeleton starter React component generated by Plasmic.
// This file is owned by you, feel free to edit as you see fit.
import ContextMenuIndicator from "@/wab/client/components/ContextMenuIndicator/ContextMenuIndicator";
import styles from "@/wab/client/components/PageSettings.module.css";
import { DataPickerEditor } from "@/wab/client/components/sidebar-tabs/ComponentProps/DataPickerEditor";
import {
  convertExprToPageMetaString,
  convertPageMetaStringToExpr,
} from "@/wab/client/components/sidebar-tabs/PageMetaPanel";
import {
  PropEditorRow,
  PropValueEditorContext,
} from "@/wab/client/components/sidebar-tabs/PropEditorRow";
import { LegacyComponentParamsSection } from "@/wab/client/components/sidebar-tabs/legacy-component-params-section";
import { ImageAssetPreviewAndPicker } from "@/wab/client/components/style-controls/ImageSelector";
import {
  DefaultPageSettingsProps,
  PlasmicPageSettings,
} from "@/wab/client/plasmic/plasmic_kit_page_settings/PlasmicPageSettings";
import { useStudioCtx } from "@/wab/client/studio-ctx/StudioCtx";
import { ViewCtx } from "@/wab/client/studio-ctx/view-ctx";
import { nullToUndefined, spawn } from "@/wab/shared/common";
import { PageComponent } from "@/wab/shared/core/components";
import {
  ExprCtx,
  asCode,
  codeLit,
  createExprForDataPickerValue,
  extractValueSavedFromDataPicker,
  getLastDynExprFromTemplatedString,
  hasDynamicParts,
  isDynamicExpr,
  mkTemplatedStringOfOneDynExpr,
} from "@/wab/shared/core/exprs";
import { ImageAssetType } from "@/wab/shared/core/image-asset-type";
import { tryEvalExpr } from "@/wab/shared/eval";
import {
  ImageAsset,
  ImageAssetRef,
  ObjectPath,
  TemplatedString,
  TplTag,
  isKnownExpr,
  isKnownImageAsset,
  isKnownImageAssetRef,
  isKnownTemplatedString,
} from "@/wab/shared/model/classes";
import { Menu } from "antd";
import { observer } from "mobx-react";
import * as React from "react";

interface PageSettingsProps extends DefaultPageSettingsProps {
  page: PageComponent;
  viewCtx: ViewCtx;
  exprCtx: ExprCtx;
}

const ImageAssetPickerWithDynamicValue = observer(
  function ImageAssetPickerWithDynamicValue({
    page,
    viewCtx,
    exprCtx,
    pageMetaEnv,
  }: {
    page: PageComponent;
    viewCtx: ViewCtx;
    exprCtx: ExprCtx;
    pageMetaEnv;
  }) {
    const sc = useStudioCtx();
    const pageMeta = page.pageMeta;
    const allowDynamicValue = sc.appCtx.appConfig.serverQueries;

    const [isDataPickerVisible, setIsDataPickerVisible] = React.useState(false);

    const openGraphImage = pageMeta.openGraphImage;

    const imageValue = isKnownImageAssetRef(openGraphImage)
      ? openGraphImage.asset
      : openGraphImage;
    const isDynamic =
      isKnownTemplatedString(openGraphImage) && hasDynamicParts(openGraphImage);

    const evaluatedImage = React.useMemo(() => {
      if (isDynamic && isKnownExpr(openGraphImage) && pageMetaEnv) {
        const evaluated = tryEvalExpr(
          asCode(openGraphImage, exprCtx).code,
          pageMetaEnv
        );
        return evaluated?.val;
      }
      return undefined;
    }, [isDynamic, openGraphImage, pageMetaEnv, exprCtx]);

    const updateOpenGraphImage = (
      raw: TemplatedString | ImageAsset | string | null
    ) => {
      const value =
        raw && isKnownImageAsset(raw) ? new ImageAssetRef({ asset: raw }) : raw;
      spawn(sc.tryChangePageMetaImage(page, value));
    };

    const switchToDynamicValue = () => {
      spawn(
        sc.changeUnsafe(() => {
          page.pageMeta.openGraphImage = mkTemplatedStringOfOneDynExpr(
            new ObjectPath({
              path: ["undefined"],
              fallback: codeLit(undefined),
            })
          );
        })
      );
      setIsDataPickerVisible(true);
    };

    const contextMenu = (
      <Menu>
        {openGraphImage && (
          <Menu.Item
            key={"remove"}
            onClick={() => {
              updateOpenGraphImage(null);
            }}
          >
            Clear image
          </Menu.Item>
        )}
        {!isDynamic && allowDynamicValue && (
          <Menu.Item
            key={"openGraphImageDynamic"}
            onClick={switchToDynamicValue}
          >
            Use dynamic value
          </Menu.Item>
        )}
      </Menu>
    );

    return (
      <ContextMenuIndicator
        menu={contextMenu}
        showDynamicValueButton={!isDynamic && allowDynamicValue}
        onIndicatorClickDefault={switchToDynamicValue}
        className="qb-custom-widget"
        fullWidth={true}
      >
        {isDynamic ? (
          <div className="flex-col">
            <DataPickerEditor
              viewCtx={viewCtx}
              value={extractValueSavedFromDataPicker(
                getLastDynExprFromTemplatedString(
                  openGraphImage as TemplatedString
                ),
                exprCtx
              )}
              onChange={(val) => {
                if (!val) {
                  return;
                }
                const dynExpr = createExprForDataPickerValue(val, undefined);
                const templatedString = mkTemplatedStringOfOneDynExpr(dynExpr);
                updateOpenGraphImage(templatedString);
              }}
              onUnlink={() => {
                updateOpenGraphImage(null);
              }}
              visible={isDataPickerVisible}
              setVisible={setIsDataPickerVisible}
              data={pageMetaEnv}
              schema={sc.customFunctionsSchema()}
            />
            {evaluatedImage && (
              <ImageAssetPreviewAndPicker
                studioCtx={sc}
                type={ImageAssetType.Picture}
                value={
                  typeof evaluatedImage === "string"
                    ? evaluatedImage
                    : undefined
                }
                keepOpen={false}
                onPicked={(picked) => {
                  updateOpenGraphImage(picked);
                }}
              />
            )}
          </div>
        ) : (
          <ImageAssetPreviewAndPicker
            studioCtx={sc}
            type={ImageAssetType.Picture}
            value={nullToUndefined(
              !isDynamic &&
                (isKnownImageAsset(imageValue) ||
                  typeof imageValue === "string")
                ? imageValue
                : undefined
            )}
            keepOpen={true}
            onPicked={(picked) => {
              updateOpenGraphImage(picked);
            }}
          />
        )}
      </ContextMenuIndicator>
    );
  }
);

const PageSettings = observer(function PageSettings({
  page,
  viewCtx,
  exprCtx,
  ...props
}: PageSettingsProps) {
  const sc = useStudioCtx();
  const pageMeta = page.pageMeta;
  const [route, setRoute] = React.useState(pageMeta.path);
  const disableDynamicValue = !sc.appCtx.appConfig.serverQueries
    ? true
    : undefined;

  const title = React.useMemo(
    () => convertPageMetaStringToExpr(page.pageMeta?.title),
    [page.pageMeta?.title]
  );
  const description = React.useMemo(
    () => convertPageMetaStringToExpr(page.pageMeta?.description),
    [page.pageMeta?.description]
  );
  const canonical = React.useMemo(
    () => convertPageMetaStringToExpr(page.pageMeta?.canonical),
    [page.pageMeta?.canonical]
  );

  const env = viewCtx.getCanvasEnvForTpl(page.tplTree);

  const pageMetaEnv = React.useMemo(() => {
    if (!env) {
      return undefined;
    }
    const { $queries, $$: _$, $state: _s, ...rest } = env;
    rest.$queries = {};
    for (const queryName of Object.keys($queries ?? {})) {
      if (page.serverQueries.find((dq) => dq.name === queryName)) {
        rest.$queries[queryName] = $queries[queryName];
      }
    }
    return rest;
  }, [env]);

  const descriptionLength = React.useMemo(() => {
    const desc = pageMeta.description;
    if (typeof desc === "string") {
      return desc.length;
    } else if (viewCtx && pageMetaEnv && isDynamicExpr(desc)) {
      const evaluated = tryEvalExpr(asCode(desc, exprCtx).code, pageMetaEnv);
      return evaluated?.val?.toString()?.length ?? 0;
    }
    return 0;
  }, [pageMeta.description]);

  return (
    <div className={styles.pageSettingsContainer}>
      <PropValueEditorContext.Provider
        value={{
          componentPropValues: {},
          ccContextData: {},
          env: undefined,
        }}
      >
        <PlasmicPageSettings
          {...props}
          routeInput={{
            value: route,
            onChange: (e) => {
              setRoute(e.target.value);
            },
            onBlur: async () => {
              await sc.tryChangePath(page, route);
              setRoute(page.pageMeta.path);
            },
          }}
          title={
            <PropEditorRow
              attr="title"
              label="Title"
              propType={{ type: "string", defaultValueHint: "Title" }}
              expr={title}
              onChange={(expr) => {
                const newTitle = convertExprToPageMetaString(expr);
                spawn(sc.tryChangePageMeta(page, "title", newTitle));
              }}
              viewCtx={viewCtx}
              tpl={page.tplTree as TplTag}
              disableLinkToProp={true}
              disableDynamicValue={disableDynamicValue}
              env={pageMetaEnv}
            />
          }
          description={
            <PropEditorRow
              attr="description"
              label="Description"
              propType={{
                type: "string",
                control: "multiLine",
                defaultValueHint: "Description",
              }}
              expr={description}
              onChange={(expr) => {
                const newDesc = convertExprToPageMetaString(expr) ?? "";
                spawn(sc.tryChangePageMetaDescription(page, newDesc));
              }}
              viewCtx={viewCtx}
              tpl={page.tplTree as TplTag}
              disableLinkToProp={true}
              disableDynamicValue={disableDynamicValue}
              env={pageMetaEnv}
            />
          }
          characterCount={{
            children: descriptionLength,
          }}
          canonical={
            <PropEditorRow
              attr="canonical"
              label="Canonical URL"
              propType={{ type: "string", defaultValueHint: "Canonical URL" }}
              expr={canonical}
              onChange={(expr) => {
                const newCanonical = convertExprToPageMetaString(expr);
                spawn(sc.tryChangePageMeta(page, "canonical", newCanonical));
              }}
              viewCtx={viewCtx}
              tpl={page.tplTree as TplTag}
              disableLinkToProp={true}
              disableDynamicValue={disableDynamicValue}
              env={pageMetaEnv}
            />
          }
          imageAssetPicker={{
            render: () => (
              <ImageAssetPickerWithDynamicValue
                page={page}
                viewCtx={viewCtx}
                exprCtx={exprCtx}
                pageMetaEnv={pageMetaEnv}
              />
            ),
          }}
          propsControl={{
            render: () => (
              <LegacyComponentParamsSection studioCtx={sc} component={page} />
            ),
          }}
        />
      </PropValueEditorContext.Provider>
    </div>
  );
});
export default PageSettings;
